import React from "react";
import { Line } from "react-chartjs-2";

import { Chart as ChartJS, ChartOptions, LinearScale, ScriptableContext, ScriptableLineSegmentContext } from "chart.js";
import { Context } from "chartjs-plugin-datalabels";
import moment from "moment";

import { getLanguageFromLocalStorage, useTranslation } from "utils";

import { LOCALE_DICT } from "consts";

import { CarbonProgram, NullableRangeObject } from "models";

import { normalizeDataWithBaseline } from "pages/app/field-timeline/utils/EvaluationMetricUtils";

const COLORS = {
  transparent: "rgba(0,0,0,0)",
  white: "#fff",
  black: "#100C06",
  "green-100": "#EBF2F0",
  "green-400": "#065C45",
  "red-500": "#DB3804",
};

const FONT = {
  family: "dazzed",
  size: 10,
};

type ChartProps = {
  datasets: {
    label: string;
    data: NullableRangeObject;
  }[];
  reportingYear: number;
  selectedYear: string;
  baseline?: number;
  reversedColorScheme?: boolean;
  unitOfMeasure?: string;
  reportingPeriod?: CarbonProgram["fieldsManagement"]["reportingPeriod"];
  width?: number;
  height?: number;
};

ChartJS.register(LinearScale);

export const FieldTimelineLineChartWithRanges = ({
  datasets,
  reportingYear,
  selectedYear,
  baseline = 0,
  reversedColorScheme,
  unitOfMeasure,
  reportingPeriod = { start: "01/01", end: "12/31" },
  width,
  height,
}: ChartProps) => {
  const { t } = useTranslation("fieldTimeline");
  const language = getLanguageFromLocalStorage({ forDates: true });
  const locale = LOCALE_DICT[language] ? LOCALE_DICT[language] : LOCALE_DICT.de;

  const years = datasets.map((dataset) => parseInt(dataset.label));
  const reportingYearDataIndex = years.findIndex((year) => year === reportingYear);

  const isAllYearsView = Number.isNaN(Number(selectedYear));

  const reportingPeriodStartMonth = parseInt(reportingPeriod.start.split("/")[0]);
  const reportingPeriodEndMonth = parseInt(reportingPeriod.end.split("/")[0]);

  const relevantDatasets = isAllYearsView
    ? normalizeDataWithBaseline(datasets, baseline)
    : normalizeDataWithBaseline(
        datasets
          .filter((dataset) => dataset.label === selectedYear || Number(selectedYear) - 1 === Number(dataset.label))
          .map((relevantDataset, i) => ({
            label:
              i === 0
                ? moment()
                    .locale(locale)
                    .month(reportingPeriodStartMonth - 1)
                    .format("MMM")
                : moment()
                    .locale(locale)
                    .month(reportingPeriodEndMonth - 1)
                    .format("MMM"),
            data: { ...relevantDataset.data },
          })),
        baseline
      );

  // make sure to always display the baseline line in the graph, even when the filtered datasets don't include the baseline year
  const allValues = normalizeDataWithBaseline(datasets, baseline)
    .flatMap((dataset) => [dataset.data.min, dataset.data.avg, dataset.data.max])
    .filter((val) => val !== null) as number[];

  const dataMin = allValues.length > 0 ? Math.min(...allValues) : 0;
  const dataMax = allValues.length > 0 ? Math.max(...allValues) : 0;

  const yAxisMin = Math.min(dataMin, 0);
  const yAxisMax = Math.max(dataMax, 0);
  // add 2.5% padding to top and bottom
  const range = yAxisMax - yAxisMin;
  const padding = range * 0.025;
  const yAxisMinWithPadding = yAxisMin - padding;
  const yAxisMaxWithPadding = yAxisMax + padding;

  const getPreviousAvailableValue = (currentIndex: number): number => {
    for (let i = currentIndex - 1; i >= 0; i -= 1) {
      const value = relevantDatasets[i]?.data.avg;
      if (value !== null && value !== undefined) {
        return value;
      }
    }

    return baseline;
  };

  const chartOptions: ChartOptions<"line"> = {
    plugins: {
      datalabels: {
        formatter: (_value: number, ctx: Context) => {
          if (ctx.dataset.label === "baseline" && ctx.dataIndex === 0) {
            // hacky hack to vertically align the label to the baseline first data point by adding 2 empty lines of text
            return [t("impactPanel.evaluationMetrics.baseline"), "▼", " ", " "];
          }

          return null;
        },
        align: "right",
        anchor: "end",
        offset: -9,
        font: FONT,
        color: COLORS.black,
      },
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => `${Number(context.formattedValue) >= 0 ? "+" : ""}${context.formattedValue} ${unitOfMeasure}`,
        },
        filter: (context) => context.dataset.label !== "baseline",
      },
    },
    layout: {
      padding: {
        left: -10,
      },
    },
    scales: {
      x: {
        border: {
          display: false,
          dash: [1, 5],
        },
        grid: {
          color: COLORS.black,
          drawTicks: false,
          z: 1,
        },
        ticks: {
          padding: 10,
          font: FONT,
          color: COLORS.black,
        },
      },
      y: {
        border: {
          display: false,
        },
        grid: {
          color: COLORS.transparent,
          drawTicks: false,
          z: 1,
        },
        ticks: {
          padding: 10,
          font: FONT,
          color: COLORS.black,
          callback: (tickValue) => {
            const numericTickValue = typeof tickValue === "number" ? tickValue : Number(tickValue);
            const numberOfDecimals = yAxisMaxWithPadding < 1 && yAxisMinWithPadding > -1 ? 2 : yAxisMaxWithPadding < 10 && yAxisMinWithPadding > -10 ? 1 : 0;
            const sign = numericTickValue >= 0 ? "+" : "";

            return `${sign}${Number(numericTickValue).toFixed(numberOfDecimals)}`;
          },
        },
        suggestedMin: yAxisMinWithPadding,
        suggestedMax: yAxisMaxWithPadding,
      },
    },
    spanGaps: true,
    responsive: false,
    maintainAspectRatio: false,
    devicePixelRatio: 4,
    animation: {
      duration: 300,
    },
    locale,
  };

  const data = {
    labels: relevantDatasets.map((dataset) => dataset.label),
    datasets: relevantDatasets.reduce(
      (acc, dataset) => {
        acc[0].data.push(dataset.data.min);
        acc[1].data.push(dataset.data.avg);
        acc[2].data.push(dataset.data.max);
        // TODO: use baseline value here
        acc[3].data.push(0);
        return acc;
      },
      [
        {
          label: "min",
          data: [] as (number | null)[],
          borderColor: COLORS.transparent,
          backgroundColor: COLORS["green-100"],
          fill: "1",
          pointStyle: false as const,
          pointRadius: 3,
          pointHoverRadius: 3,
          order: 2,
        },
        {
          label: "avg",
          data: [] as (number | null)[],
          borderColor: COLORS["green-400"],
          backgroundColor: COLORS["green-100"],
          segment: {
            borderDash: (ctx: ScriptableLineSegmentContext) => {
              if (!isAllYearsView && Number(selectedYear) > reportingYear) return [2, 2];
              if (reportingYearDataIndex >= 0 && ctx.p0DataIndex >= reportingYearDataIndex) return [2, 2];
              return undefined;
            },
          },
          pointBackgroundColor: (ctx: ScriptableContext<"line">) => {
            if (ctx.raw === null) return undefined;

            // filled green point for the first data point (baseline)
            if (ctx.dataIndex === 0) return COLORS["green-400"];
            // filled white point for future years
            if (ctx.dataIndex > reportingYearDataIndex) return COLORS.white;

            const previousAvailableValue = getPreviousAvailableValue(ctx.dataIndex);
            const isNegativeComparedToPreviousYear = !reversedColorScheme
              ? (ctx.raw as number) < previousAvailableValue
              : (ctx.raw as number) > previousAvailableValue;

            // filled green point for positive change compared to previous year, red point for negative change
            return isNegativeComparedToPreviousYear ? COLORS["red-500"] : COLORS["green-400"];
          },
          pointBorderColor: (ctx: ScriptableContext<"line">) => {
            if (ctx.raw === null) return undefined;

            // green border for the first data point (baseline)
            if (ctx.dataIndex === 0) return COLORS["green-400"];

            const previousAvailableValue = getPreviousAvailableValue(ctx.dataIndex);
            const isNegativeComparedToPreviousYear = !reversedColorScheme
              ? (ctx.raw as number) < previousAvailableValue
              : (ctx.raw as number) > previousAvailableValue;

            // green border for positive change compared to previous year, red border for negative change
            return isNegativeComparedToPreviousYear ? COLORS["red-500"] : COLORS["green-400"];
          },
          pointRadius: 3,
          pointHoverRadius: 3,
          borderWidth: 1,
          order: 1,
        },
        {
          label: "max",
          data: [] as (number | null)[],
          borderColor: COLORS.transparent,
          backgroundColor: COLORS["green-100"],
          fill: "-1",
          pointStyle: false as const,
          pointRadius: 3,
          pointHoverRadius: 3,
          order: 2,
        },
        // add a fake dataset to draw the baseline line on the graph
        {
          label: "baseline",
          data: [] as (number | null)[],
          segment: { borderDash: [1, 5] },
          borderColor: COLORS.black,
          backgroundColor: COLORS.transparent,
          fill: "0",
          pointStyle: false as const,
          pointRadius: 3,
          pointHoverRadius: 3,
          borderWidth: 1,
          order: 0,
        },
      ]
    ),
  };

  return <Line options={chartOptions} data={data} data-testid="field-timeline-line-chart-with-ranges" width={width} height={height} />;
};
